import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SelectField } from '../../../components/ui/SelectField';
import type { SurveyStep } from '../../../types/survey';
import type { SurveyResponse } from '../../../types/surveyResponse';
import type { SurveyResultRecipient, SurveyResultSend } from '../../../types/surveyResults';
import { formatAnswer } from '../public/SubmittedView';

// Phase 6B E6 — individual responses table + detail drawer (§6.6). One row per
// recipient (incl. pending/expired). Own status + search filters (distinct from
// the audience breakdown filter, which slices the aggregate question cards). Row
// click → a right drawer with the full per-question answer detail.

const STATUS_LABEL: Record<SurveyResultRecipient['responseStatus'], string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  submitted: 'Submitted',
  expired: 'Expired',
};
const STATUS_FILTERS = ['all', 'submitted', 'pending', 'in_progress', 'expired'] as const;

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function IndividualResponses({
  recipients,
  sends,
  responses,
  steps,
}: {
  recipients: SurveyResultRecipient[];
  sends: SurveyResultSend[];
  responses: SurveyResponse[];
  steps: SurveyStep[];
}) {
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const channelsByRecipient = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const s of sends) {
      const set = m.get(s.recipientId) ?? new Set<string>();
      set.add(s.channel);
      m.set(s.recipientId, set);
    }
    return m;
  }, [sends]);

  const responseByRecipient = useMemo(
    () => new Map(responses.map((r) => [r.recipientId, r])),
    [responses],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recipients.filter(
      (r) =>
        (status === 'all' || r.responseStatus === status) &&
        (q === '' || r.displayName.toLowerCase().includes(q) || r.accountName.toLowerCase().includes(q)),
    );
  }, [recipients, status, search]);

  const openRecipient = openId ? recipients.find((r) => r.id === openId) ?? null : null;

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-sm font-semibold text-slate-800">Individual responses</h2>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SelectField
          size="sm"
          value={status}
          onValueChange={(v) => setStatus(v as typeof status)}
          options={STATUS_FILTERS.map((s) => ({ value: s, label: s === 'all' ? 'All statuses' : STATUS_LABEL[s as SurveyResultRecipient['responseStatus']] }))}
        />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / account…"
          className="w-56 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Recipient</th>
              <th className="px-4 py-2 font-medium">Account</th>
              <th className="px-4 py-2 font-medium">Channels</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-4 text-slate-400">No recipients match.</td></tr>
            ) : (
              filtered.map((r) => {
                const channels = [...(channelsByRecipient.get(r.id) ?? [])].join(' + ') || '—';
                const submittable = r.responseStatus === 'submitted';
                return (
                  <tr
                    key={r.id}
                    onClick={() => submittable && setOpenId(r.id)}
                    className={submittable ? 'cursor-pointer hover:bg-slate-50' : ''}
                  >
                    <td className="px-4 py-2 font-medium text-slate-800">{r.displayName}</td>
                    <td className="px-4 py-2 text-slate-600">{r.accountName}</td>
                    <td className="px-4 py-2 text-slate-600">{channels}</td>
                    <td className="px-4 py-2 text-slate-600">{STATUS_LABEL[r.responseStatus]}</td>
                    <td className="px-4 py-2 text-slate-600">{fmtDate(r.submittedAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {openRecipient && (
        <ResponseDrawer
          recipient={openRecipient}
          response={responseByRecipient.get(openRecipient.id) ?? null}
          steps={steps}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

function ResponseDrawer({
  recipient,
  response,
  steps,
  onClose,
}: {
  recipient: SurveyResultRecipient;
  response: SurveyResponse | null;
  steps: SurveyStep[];
  onClose: () => void;
}) {
  const byStep = new Map((response?.answers ?? []).map((a) => [a.stepId, a]));
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h3 className="text-base font-semibold text-slate-800">{recipient.displayName}</h3>
            <p className="text-xs text-slate-400">{recipient.accountName}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <dl className="divide-y divide-slate-100">
            {steps.map((s) => {
              if (s.kind !== 'question') return null;
              const a = byStep.get(s.id);
              if (!a) return null;
              return (
                <div key={s.id} className="py-3">
                  <dt className="text-sm font-medium text-slate-700">{s.prompt}</dt>
                  <dd className="mt-1 text-sm text-slate-900">{formatAnswer(s, a.value)}</dd>
                  {a.comment && <dd className="mt-1 text-xs italic text-slate-500">“{a.comment}”</dd>}
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    </div>
  );
}
